/**
 * Multi-layered adblock detection module.
 *
 * Detection methods:
 * 1. Bait Resource Detection - Load scripts from ad-like URL paths
 * 2. DOM Honeypot Detection - Check if ad-related elements are hidden by CSS
 * 3. Ad Provider Detection - Verify ad provider scripts loaded and initialized
 *
 * Two-tier detection:
 * - suspected: 1+ signals fired (modal-safe, non-destructive)
 * - confirmed: provider signal OR 2+ independent signals (zone collapse, content hiding)
 *
 * State is kept in closure scope to prevent easy bypass via console.
 */

import { BAIT_PATHS } from './adblockConstants';

export interface AdblockDetectionResult {
  suspected: boolean;
  confirmed: boolean;
}

declare global {
  interface Window {
    RIO_OnProviderBlocked?: () => void;
    RIO_OnProviderLoaded?: () => void;
    __RIO_PROVIDER_BLOCKED?: boolean;
    __RIO_PROVIDER_LOADED?: boolean;
    __RIO_BAIT_LOADED?: boolean;
    __RIO_ADBLOCK_DETECTOR_INITIALIZED?: boolean;
    ramp?: { que: unknown[] };
    __vm?: unknown;
    gtag?: (...args: unknown[]) => void;
  }
}

// Private module-scoped state - not accessible from window/console
let detectionResult: AdblockDetectionResult | null = null;

// Honeypot elements - class names and IDs commonly hidden by ad blockers
const HONEYPOT_CLASSES = ['ad-banner', 'adsbox', 'advertisement', 'textads banner-ads'];
const HONEYPOT_IDS = ['google_ads_frame', 'ad-container-main'];

class AdblockDetector {
  private signals = { bait: false, honeypot: false, provider: false };
  private finalized = false;
  private honeypots: HTMLElement[] = [];

  /**
   * Initialize the adblock detector. Call this once on page load.
   */
  init(): void {
    if (window.__RIO_ADBLOCK_DETECTOR_INITIALIZED) {
      return;
    }
    window.__RIO_ADBLOCK_DETECTOR_INITIALIZED = true;

    this.setupProviderCallbacks();
    this.createHoneypots();
    this.loadBaitScript();

    // Run detection when DOM is ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.runDetection(false));
    } else {
      this.runDetection(false);
    }

    // Final check after delay to catch timing-based blocking
    setTimeout(() => this.runDetection(true), 2500);
  }

  /**
   * Set up callbacks for ad provider script load/error events.
   * Also picks up early signals captured by the inline HTML stub before this bundle loaded.
   */
  private setupProviderCallbacks(): void {
    // Check for early signals captured by detectAdBlocker.html before bundle loaded
    if (window.__RIO_PROVIDER_BLOCKED) {
      this.signals.provider = true;
    }

    // Replace stubs with real handlers
    window.RIO_OnProviderBlocked = () => {
      this.signals.provider = true;
      this.evaluateAndPublish();
    };
    window.RIO_OnProviderLoaded = () => {
      // Provider loaded - don't mark as blocked
    };
  }

  /**
   * Create invisible honeypot elements with ad-related class names.
   */
  private createHoneypots(): void {
    const style = 'position:absolute;top:-999px;left:-999px;height:1px;width:1px;';

    const createHoneypot = (attr: 'className' | 'id', value: string) => {
      const el = document.createElement('div');
      el[attr] = value;
      el.style.cssText = style;
      document.body.appendChild(el);
      this.honeypots.push(el);
    };

    HONEYPOT_CLASSES.forEach((c) => { createHoneypot('className', c); });
    HONEYPOT_IDS.forEach((id) => { createHoneypot('id', id); });
  }

  /**
   * Load a bait script from a randomly chosen ad-like URL.
   */
  private loadBaitScript(): void {
    const path = BAIT_PATHS[Math.floor(Math.random() * BAIT_PATHS.length)];
    const script = document.createElement('script');
    script.src = path;
    script.async = true;
    script.onerror = () => {
      this.signals.bait = true;
      this.evaluateAndPublish();
    };
    document.head.appendChild(script);
  }

  /**
   * Check if a honeypot element has been hidden by CSS rules.
   */
  private isElementHidden(el: HTMLElement): boolean {
    const style = window.getComputedStyle(el);
    return (
      style.display === 'none' ||
      style.visibility === 'hidden' ||
      style.opacity === '0' ||
      el.offsetHeight === 0 ||
      el.offsetWidth === 0 ||
      el.offsetParent === null
    );
  }

  /**
   * Run all detection checks.
   */
  private runDetection(isFinal: boolean): void {
    // Check if any honeypot was hidden
    if (this.honeypots.some((el) => this.isElementHidden(el))) {
      this.signals.honeypot = true;
    }

    // Only check bait script in final detection - it loads async and needs time
    if (isFinal) {
      if (!window.__RIO_BAIT_LOADED) {
        this.signals.bait = true;
      }
      this.finalized = true;
      this.evaluateAndPublish();
    } else {
      // Early evaluation for fast signals (honeypot, provider callback)
      this.evaluateAndPublish();
    }
  }

  /**
   * Evaluate signals and publish the detection result.
   *
   * suspected = 1+ signals (safe for non-destructive actions like modal)
   * confirmed = provider signal OR 2+ independent signals (safe for destructive actions)
   */
  private evaluateAndPublish(): void {
    const signalCount = [this.signals.bait, this.signals.honeypot, this.signals.provider].filter(Boolean).length;
    const suspected = signalCount >= 1;
    const confirmed = this.signals.provider || signalCount >= 2;

    // Before finalization, only publish if we have actionable signals or state changed
    if (!this.finalized) {
      if (!suspected) {
        return;
      }
      if (
        detectionResult !== null &&
        detectionResult.suspected === suspected &&
        detectionResult.confirmed === confirmed
      ) {
        return;
      }
    }

    detectionResult = { suspected, confirmed };

    // Dispatch event for React components
    window.dispatchEvent(
      new CustomEvent('rio:adblock-detection-complete', {
        detail: { suspected, confirmed }
      })
    );

    if (typeof window.gtag === 'function') {
      const label = confirmed ? 'confirmed' : suspected ? 'suspected' : 'false';
      window.gtag('set', 'user_properties', { blocking_ads: label });
    }
  }
}

/**
 * Get the current adblock detection state.
 * Returns null if detection hasn't completed yet.
 * This reads from closure-scoped state, not window.RIO_ProviderBlocked.
 */
export function getAdblockState(): AdblockDetectionResult | null {
  return detectionResult;
}

export const adblockDetector = new AdblockDetector();

// Auto-initialize when script runs
if (typeof window !== 'undefined' && typeof document !== 'undefined') {
  if (document.body) {
    adblockDetector.init();
  } else {
    document.addEventListener('DOMContentLoaded', () => adblockDetector.init());
  }
}
